import React, { useState, useEffect, useMemo, useCallback } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
import API_BASE from "./apiBase";

const API = `${API_BASE}`;

/**
 * Holiday rota — one tab per branch, month or week at a time.
 *
 * Each day cell shows every member of staff with a state: working, on leave, or
 * absent. Those states come from three sources and they do not agree with each
 * other by design, so the source is always shown:
 *
 *   clocked    an actual clocking exists — the strongest evidence there is
 *   leave      an approved or pending leave_requests day
 *   absence    an absences day, with its reason
 *   assumed    nothing recorded at all
 *
 * That last one matters. Where no clocking exists we assume the person worked
 * rather than greying them out: a clock terminal that was down for a day would
 * otherwise wipe the whole team off the rota, and a rota nobody trusts is worse
 * than no rota. It is marked "assumed" so the difference is visible.
 *
 * REMOVING A DAY (v2)
 * -------------------
 * Expanding a cell lists everybody on it; leave and absence rows now carry a ✕
 * that takes that ONE day off the underlying record. Not the whole record — a
 * week off with one day cancelled is still a four-day week off — so the server
 * pulls the day from days[] and only withdraws the record when the last day
 * goes.
 *
 * It asks first, and it says what it is about to do rather than "Are you sure?".
 * The confirmation carries an optional reason, and every removal is written to
 * the tenant collection holiday_rota_logs with the day as it stood. This is the
 * kind of edit that gets disputed weeks later, so the day's own change history
 * is shown at the foot of the expanded cell. Requires holiday_rota_routes v2
 * (/api/holidayRota/removeDay and /api/holidayRota/logs).
 */

const LEAVE_COLOURS = {
  FULL_DAY: "#3b82f6",
  HALF_DAY_MORNING: "#60a5fa",
  HALF_DAY_AFTERNOON: "#93c5fd",
  CUSTOM_HOURS: "#1560ac",
};
const ABSENCE_COLOURS = {
  SICK_LEAVE: "#dc2626",
  UNAUTHORISED: "#b45309",
  EMERGENCY: "#ea580c",
  OTHER: "#6b7280",
};
const ROLE_PALETTE = [
  "#1976d2", "#7c3aed", "#be185d", "#a16207", "#1560ac",
  "#15803d", "#c2410c", "#4338ca", "#0369a1", "#9d174d",
];

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

const pretty = (s) =>
  (s || "").toString().replace(/_/g, " ").toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const iso = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const isoUTC = (d) => {
  const x = new Date(d);
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`;
};

/** "HH:MM" -> minutes past midnight. */
const hhmm = (s) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(s || "").trim());
  return m ? (+m[1]) * 60 + (+m[2]) : 0;
};

/**
 * The agreed pattern for one person on one date.
 *
 * `occurrence` is the Nth of that weekday in the month, which is how a rotating
 * pattern is expressed. Every branch in the sample has all five occurrences
 * identical, so nobody is on a rotation yet - but the lookup honours it, and
 * falls back to occurrence 1 rather than treating a gap as a day off.
 *
 * start == end means not rostered. That is a real day off, and it is not the
 * same thing as holiday.
 */
const patternFor = (times, staffId, date) => {
  if (!times || times.length === 0) return null;
  const weekday = date.getDay();                       // 0 = Sunday
  const occurrence = Math.min(Math.ceil(date.getDate() / 7), 5);

  const rows = times.filter((t) => t.staffId === staffId && t.weekday === weekday);
  if (rows.length === 0) return null;

  const row = rows.find((t) => t.occurrence === occurrence) || rows[0];
  const startM = hhmm(row.start);
  const endM = hhmm(row.end);
  if (endM <= startM) return { rostered: false, hours: 0, row };

  // paid breaks stay in the contracted total; unpaid ones come out
  const mins = (endM - startM) - (row.breaksPaid ? 0 : (row.breakMins || 0));
  return { rostered: true, hours: Math.max(mins, 0) / 60, row };
};

const hoursBetween = (a, b) => {
  if (!a || !b) return 0;
  const h = (new Date(b) - new Date(a)) / 3600000;
  return h > 0 && h < 24 ? h : 0;
};

const HolidayRota = () => {
  const masked = useMaskNames();

  const [branches, setBranches] = useState([]);
  const [activeBranch, setActiveBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [view, setView] = useState("month");        // month | week | year
  // "off" is not scheduled - a rostered day off. Hidden by default, otherwise
  // every cell lists the whole team on their days off and the signal is lost.
  const [show, setShow] = useState({ working: true, holiday: true, absent: true, off: false });
  const [dayDetail, setDayDetail] = useState(null); // dayKey of the open dialog
  // The cover a branch is willing to run on. Below it the cell goes red.
  const [target, setTarget] = useState(80);
  const [basis, setBasis] = useState("auto");       // auto | hours | days
  // Which roles count towards cover. Drivers being short does not make a
  // pharmacy unable to open; a pharmacist being short does.
  const [coverRoles, setCoverRoles] = useState(null);  // null = all

  const [panelTab, setPanelTab] = useState("outstanding");  // outstanding | completed | allowance
  const [allowance, setAllowance] = useState(null);
  const [allowSort, setAllowSort] = useState({ by: "name", dir: 1 });
  const [reqDetail, setReqDetail] = useState(null);   // the request being decided
  const [actionResult, setActionResult] = useState(null);
  const [addOpen, setAddOpen] = useState(null);     // 'leave' | 'absence' | null
  // Bumped after any write, to re-pull the window rather than patch state by
  // hand — the grid is derived from four collections and second-guessing what
  // a removal did to the derived figures is how they drift apart.
  const [reloadKey, setReloadKey] = useState(0);
  const [removeTarget, setRemoveTarget] = useState(null);  // entry being removed
  const [dayLog, setDayLog] = useState(null);              // log rows for the open day

  const token = localStorage.getItem("authToken");

  const post = useCallback(async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const d = await post("/api/holidayRota/branches", {});
        const list = d.branches || [];
        setBranches(list);
        if (list.length) setActiveBranch(list[0]);
      } catch (e) {
        setError(`Could not load branches: ${e.message}`);
      }
    })();
  }, [post]);

  /* --------------------------------------------------------- window dates */

  const windowRange = useMemo(() => {
    if (view === "year") {
      return { from: new Date(cursor.getFullYear(), 0, 1),
               to: new Date(cursor.getFullYear(), 11, 31) };
    }
    if (view === "week") {
      const s = new Date(cursor);
      const dow = (s.getDay() + 6) % 7;          // Monday = 0
      s.setDate(s.getDate() - dow);
      const e = new Date(s);
      e.setDate(s.getDate() + 6);
      return { from: s, to: e };
    }
    return { from: new Date(cursor.getFullYear(), cursor.getMonth(), 1),
             to: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0) };
  }, [cursor, view]);

  useEffect(() => {
    if (!activeBranch) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await post("/api/holidayRota/data", {
          dbName: activeBranch.dbName,
          pharmacyId: activeBranch.pharmacyId,
          from: iso(windowRange.from),
          to: iso(windowRange.to),
        });
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(`Could not load rota: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBranch, windowRange, post, reloadKey]);

  /* ------------------------------------------- removing a leave/absence day */

  /** The day's change history, shown at the foot of the expanded cell. */
  const loadDayLog = useCallback(async (key) => {
    if (!activeBranch || !key) { setDayLog(null); return; }
    try {
      const d = await post("/api/holidayRota/logs", {
        dbName: activeBranch.dbName,
        pharmacyId: activeBranch.pharmacyId,
        dateKey: key,
        limit: 25,
      });
      setDayLog(d.logs || []);
    } catch (_) {
      setDayLog([]);   // no history is not an error worth shouting about
    }
  }, [activeBranch, post]);

  useEffect(() => {
    if (dayDetail) loadDayLog(dayDetail); else setDayLog(null);
  }, [dayDetail, loadDayLog, reloadKey]);

  /**
   * Remove one day from one leave request or absence. Confirmed already —
   * removeTarget is only set from the confirmation dialogue.
   */
  const removeDay = useCallback(async (entry, reason) => {
    if (!activeBranch || !entry?.recordId) return;
    try {
      const r = await post("/api/holidayRota/removeDay", {
        dbName: activeBranch.dbName,
        pharmacyId: activeBranch.pharmacyId,
        kind: entry.recordKind,
        recordId: entry.recordId,
        date: entry.dateKey,
        reason: reason || null,
      });
      setRemoveTarget(null);
      setReloadKey((k) => k + 1);
      return r;
    } catch (e) {
      setError(`Could not remove that day: ${e.message}`);
      setRemoveTarget(null);
      throw e;
    }
  }, [activeBranch, post]);

  const loadAllowance = useCallback(async () => {
    if (!activeBranch) return;
    try {
      const d = await post("/api/holidayRota/allowance", {
        dbName: activeBranch.dbName,
        pharmacyId: activeBranch.pharmacyId,
        year: cursor.getFullYear(),
      });
      setAllowance(d);
    } catch (e) {
      setError(`Could not load allowances: ${e.message}`);
    }
  }, [activeBranch, cursor, post]);

  useEffect(() => {
    if (panelTab === "allowance") loadAllowance();
  }, [panelTab, loadAllowance]);

  /* ------------------------------------------------- compose the day grid */

  const roleColour = useMemo(() => {
    const m = new Map();
    (data?.roles || []).forEach((r, i) => m.set(r.id, ROLE_PALETTE[i % ROLE_PALETTE.length]));
    return m;
  }, [data]);

  /**
   * dayKey -> [{staff, state, source, detail, hours}]
   *
   * Precedence: absence beats leave beats clocking. Somebody off sick on a day
   * they had booked off is still off sick, and that is the fact a manager needs.
   */
  const byDay = useMemo(() => {
    if (!data) return {};
    const out = {};
    const staffById = new Map(data.staff.map((s) => [s.id, s]));

    const put = (key, entry) => {
      if (!out[key]) out[key] = [];
      out[key].push(entry);
    };

    const leaveIdx = new Map();
    for (const l of data.leave) {
      for (const d of l.days) {
        const k = `${isoUTC(d.date)}|${l.staffId}`;
        leaveIdx.set(k, { ...d, status: l.status, reason: l.reason, id: l.id });
      }
    }
    const absIdx = new Map();
    for (const a of data.absences) {
      for (const d of a.days) {
        const k = `${isoUTC(d.date)}|${a.staffId}`;
        absIdx.set(k, { ...d, notes: a.notes, id: a.id });
      }
    }
    const clockIdx = new Map();
    for (const a of data.attendance) {
      const k = `${isoUTC(a.clockIn)}|${a.staffId}`;
      const prev = clockIdx.get(k);
      if (!prev) clockIdx.set(k, a);
    }

    const cur = new Date(windowRange.from);
    while (cur <= windowRange.to) {
      const key = iso(cur);
      for (const s of data.staff) {
        const kk = `${key}|${s.id}`;
        const abs = absIdx.get(kk);
        const lv = leaveIdx.get(kk);
        const ck = clockIdx.get(kk);
        const pat = patternFor(data.times, s.id, cur);
        const planned = pat ? pat.hours : 7.5;   // no pattern on file: assume a full day

        // not rostered today. Distinct from holiday, and worth keeping out of
        // the way rather than pretending it is an absence.
        if (pat && !pat.rostered && !abs && !lv && !ck) {
          put(key, {
            staff: s, state: "off", source: "pattern",
            label: "Not scheduled",
            detail: "No agreed hours for this day",
            colour: "#cbd5e1", planned: 0, hours: 0,
          });
          continue;
        }

        if (abs) {
          put(key, {
            staff: s, state: "absent", source: "absence",
            label: pretty(abs.absenceType),
            detail: abs.notes || null,
            colour: ABSENCE_COLOURS[abs.absenceType] || ABSENCE_COLOURS.OTHER,
            planned, hours: 0,
            // what the ✕ in the expanded cell needs to act on
            recordKind: "absence", recordId: abs.id, dateKey: key,
          });
        } else if (lv) {
          const half = (lv.leaveType || "").startsWith("HALF_DAY");
          put(key, {
            staff: s, state: "holiday", source: "leave",
            label: pretty(lv.leaveType),
            detail: lv.status === "PENDING" ? "Request not yet approved" : lv.reason,
            pending: lv.status === "PENDING",
            colour: LEAVE_COLOURS[lv.leaveType] || LEAVE_COLOURS.FULL_DAY,
            planned,
            // what is left of the agreed day once the leave is taken out
            hours: lv.leaveType === "CUSTOM_HOURS"
              ? Math.max(0, planned - hoursBetween(lv.startTime, lv.endTime))
              : half ? planned / 2 : 0,
            recordKind: "leave", recordId: lv.id, dateKey: key,
          });
        } else if (ck) {
          put(key, {
            staff: s, state: "working", source: "clocked",
            label: "Clocked in",
            detail: ck.clockOut
              ? `${new Date(ck.clockIn).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` +
                ` – ${new Date(ck.clockOut).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
              : "still clocked in",
            colour: "#16a34a",
            planned,
            hours: ck.clockOut ? hoursBetween(ck.clockIn, ck.clockOut) : planned,
          });
        } else {
          put(key, {
            staff: s, state: "working", source: "assumed",
            label: "Assumed working",
            detail: "No clocking recorded — treated as working",
            colour: "#9ca3af",
            planned, hours: planned,
          });
        }
      }
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [data, windowRange]);

  const visible = (e) =>
    (e.state === "working" && show.working) ||
    (e.state === "holiday" && show.holiday) ||
    (e.state === "absent" && show.absent) ||
    (e.state === "off" && show.off);

  const pending = useMemo(
    () => (data?.leave || []).filter((l) => l.status === "PENDING"),
    [data]
  );
  const completed = useMemo(
    () => (data?.leave || []).filter((l) => l.status !== "PENDING"),
    [data]
  );

  const staffById = useMemo(
    () => new Map((data?.staff || []).map((s) => [s.id, s])),
    [data]
  );

  const allowanceSorted = useMemo(() => {
    const rows = [...(allowance?.staff || [])];
    const { by, dir } = allowSort;
    rows.sort((a, b) => {
      if (by === "remaining") {
        const av = a.remainingHours ?? -Infinity;
        const bv = b.remainingHours ?? -Infinity;
        return (av - bv) * dir;
      }
      return a.name.localeCompare(b.name) * dir;
    });
    return rows;
  }, [allowance, allowSort]);

  /**
   * Decide a request, then tell the person.
   *
   * The message goes through /api/headoffice/staff-messages/send - the same
   * endpoint Communications and Single staff use - so it lands in their message
   * history with the usual audit trail rather than in a private side channel
   * nobody thinks to check.
   */
  const decideRequest = async (l, status) => {
    const s = staffById.get(l.staffId);
    try {
      await post("/api/holidayRota/actionRequest", {
        dbName: activeBranch.dbName, requestId: l.id, status,
      });

      // fresh figures, so the message quotes what is actually left
      let remaining = null;
      try {
        const a = await post("/api/holidayRota/allowance", {
          dbName: activeBranch.dbName, year: cursor.getFullYear(),
        });
        setAllowance(a);
        remaining = (a.staff || []).find((x) => x.staffId === l.staffId)?.remainingHours ?? null;
      } catch (_) { /* the decision stands even if the figures do not load */ }

      const days = l.days.map((d) => isoUTC(d.date)).join(", ");
      const verdict = status === "APPROVED" ? "approved" : "not approved";
      const subject = `Holiday request ${verdict}`;
      const body =
        `Your holiday request for ${days} has been ${verdict}.\n\n` +
        (remaining != null
          ? `You have ${remaining.toFixed(1)} hours of holiday allowance remaining ` +
            `for ${cursor.getFullYear()}. Please arrange to take it before the end of the year.\n\n`
          : "") +
        `If you have any questions please speak to your manager.`;

      let emailed = false;
      let emailError = null;
      if (s?.email) {
        try {
          await post("/api/headoffice/staff-messages/send", {
            recipients: [{ dbName: activeBranch.dbName, staffId: l.staffId }],
            subject, body, method: "email",
          });
          emailed = true;
        } catch (e) {
          emailError = e.message;
        }
      } else {
        emailError = "no email address on file";
      }

      setReqDetail(null);
      setActionResult({ name: s?.name || "Unknown", status, days, remaining, emailed, emailError });
      setCursor(new Date(cursor));   // refetch the window
    } catch (e) {
      setError(`Could not update the request: ${e.message}`);
    }
  };

  /** Every staff member with allowance left, told what is outstanding. */
  const emailAllOutstanding = async () => {
    const rows = (allowance?.staff || []).filter(
      (r) => r.email && r.remainingHours != null && r.remainingHours > 0);
    if (rows.length === 0) {
      setActionResult({ bulk: true, sent: 0,
        note: "Nobody has both an email address and hours remaining." });
      return;
    }

    let sent = 0;
    const failed = [];
    for (const r of rows) {
      try {
        await post("/api/headoffice/staff-messages/send", {
          recipients: [{ dbName: activeBranch.dbName, staffId: r.staffId }],
          subject: `Your remaining holiday allowance for ${allowance.year}`,
          body:
            `Your holiday allowance for ${allowance.year} is ` +
            `${r.allowanceHours.toFixed(1)} hours.\n` +
            `You have taken ${r.takenHours.toFixed(1)} hours so far, ` +
            `leaving ${r.remainingHours.toFixed(1)} hours.\n\n` +
            (r.pendingHours > 0
              ? `A further ${r.pendingHours.toFixed(1)} hours are requested but not yet approved.\n\n`
              : "") +
            `Holiday must be taken before the end of the year, so please book ` +
            `the remainder in with your manager in good time.`,
          method: "email",
        });
        sent += 1;
      } catch (e) {
        failed.push(`${r.name}: ${e.message}`);
      }
    }
    setActionResult({ bulk: true, sent, failed });
  };

  /* ------------------------------------------------------------ rendering */

  const gridDays = useMemo(() => {
    const days = [];
    if (view === "week") {
      const c = new Date(windowRange.from);
      for (let i = 0; i < 7; i++) { days.push(new Date(c)); c.setDate(c.getDate() + 1); }
      return days;
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7;
    const c = new Date(first);
    c.setDate(c.getDate() - lead);
    for (let i = 0; i < 42; i++) { days.push(new Date(c)); c.setDate(c.getDate() + 1); }
    return days;
  }, [cursor, view, windowRange]);

  const move = (delta) => {
    const c = new Date(cursor);
    if (view === "week") c.setDate(c.getDate() + delta * 7);
    else if (view === "year") c.setFullYear(c.getFullYear() + delta);
    else c.setMonth(c.getMonth() + delta);
    setCursor(c);
  };

  // NB: index.css sets `button { color: white }` for the whole app, so every
  // button here must state its own colour or it renders white on white.
  const btn = (active) => ({
    padding: "5px 10px", marginRight: 6, cursor: "pointer",
    border: "1px solid #cbd5e1", borderRadius: 4,
    background: active ? "#1560ac" : "#fff",
    color: active ? "#fff" : "#0f172a",
  });

  /**
   * One day, summarised by role.
   *
   * Cover is measured in hours where an agreed pattern exists, and in headcount
   * where it does not. Reporting a percentage of hours when nobody has a
   * pattern on file would be a number with nothing behind it, so the basis is
   * chosen from the data and shown alongside the figure.
   */
  const dayStats = useCallback((key) => {
    const all = (byDay[key] || []);
    const rostered = all.filter((e) => e.state !== "off");

    const roles = new Map();
    for (const e of rostered) {
      const r = e.staff.role || "No role";
      if (!roles.has(r)) {
        roles.set(r, {
          role: r, roleId: e.staff.roleId,
          scheduled: 0, working: 0, holiday: 0, absent: 0,
          plannedHours: 0, availableHours: 0,
        });
      }
      const s = roles.get(r);
      s.scheduled += 1;
      if (e.state === "working") s.working += 1;
      if (e.state === "holiday") s.holiday += 1;
      if (e.state === "absent") s.absent += 1;
      s.plannedHours += e.planned ?? 0;
      s.availableHours += e.hours;
    }

    const list = [...roles.values()].sort((a, b) => a.role.localeCompare(b.role));
    const plannedHours = list.reduce((t, r) => t + r.plannedHours, 0);
    const availableHours = list.reduce((t, r) => t + r.availableHours, 0);
    const scheduled = list.reduce((t, r) => t + r.scheduled, 0);
    const working = list.reduce((t, r) => t + r.working, 0);

    const useHours = basis === "hours" || (basis === "auto" && plannedHours > 0);

    // Cover per role, then the WORST of them.
    //
    // Averaging every role into one figure hides the only thing that matters.
    // Eight drivers at 88% and three dispensers at 0% averages to a comfortable
    // 75%, which is why every day looked the same shade of red: the number was
    // dominated by headcount rather than by whether the branch can function.
    // The weakest role is the constraint, so that is what the day is judged on.
    for (const r of list) {
      r.coverage = useHours
        ? (r.plannedHours > 0 ? (r.availableHours / r.plannedHours) * 100 : null)
        : (r.scheduled > 0 ? (r.working / r.scheduled) * 100 : null);
    }

    const counted = list.filter((r) =>
      r.coverage !== null && (!coverRoles || coverRoles.includes(r.role)));

    let coverage = null;
    let weakest = null;
    for (const r of counted) {
      if (coverage === null || r.coverage < coverage) {
        coverage = r.coverage;
        weakest = r.role;
      }
    }

    return {
      roles: list, plannedHours, availableHours, scheduled, working,
      holiday: list.reduce((t, r) => t + r.holiday, 0),
      absent: list.reduce((t, r) => t + r.absent, 0),
      coverage, weakest, basis: useHours ? "hours" : "days",
      hasAnyone: rostered.length > 0,
    };
  }, [byDay, basis, coverRoles]);

  /** Below target reads red, at or above reads green. */
  const coverTint = (cov) => {
    if (cov === null || cov === undefined) return "#fff";
    return cov < target ? "#fee2e2" : "#dcfce7";
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Holiday rota</h2>
        <MaskToggleButton />
      </div>

      <p style={{ fontSize: "0.9em", color: "#555", marginTop: 6, maxWidth: 940 }}>
        Who is in, who is off and why, by branch. Attendance is taken from actual
        clockings where they exist; where nothing was recorded the person is shown
        as <em>assumed working</em> rather than dropped, so a quiet clock terminal
        never empties the rota.
      </p>

      {/* branch tabs */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", margin: "12px 0",
                    borderBottom: "2px solid #e2e8f0", paddingBottom: 6 }}>
        {branches.map((b) => (
          <button key={b.dbName} onClick={() => setActiveBranch(b)}
            style={{
              padding: "6px 12px", cursor: "pointer", border: "1px solid #cbd5e1",
              borderBottom: activeBranch?.dbName === b.dbName ? "3px solid #1560ac" : "1px solid #cbd5e1",
              borderRadius: "4px 4px 0 0",
              background: activeBranch?.dbName === b.dbName ? "#eff6ff" : "#fff",
              color: activeBranch?.dbName === b.dbName ? "#1560ac" : "#0f172a",
              fontWeight: activeBranch?.dbName === b.dbName ? "bold" : "normal",
            }}>
            {displayName(b.name, masked)}
          </button>
        ))}
        {branches.length === 0 && <span style={{ color: "#666" }}>Loading branches…</span>}
      </div>

      {/* controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    marginBottom: 12 }}>
        {/* in week view the arrows step a week, so month steps get their own
            pair - otherwise there is no way to jump a month while in week view */}
        {view === "week" && (
          <button style={btn(false)} title="Back one month"
                  onClick={() => { const c = new Date(cursor); c.setMonth(c.getMonth() - 1); setCursor(c); }}>«</button>
        )}
        <button style={btn(false)} title={view === "week" ? "Back one week" : "Back"}
                onClick={() => move(-1)}>‹</button>
        <button style={btn(false)} onClick={() => setCursor(new Date())}>Today</button>
        <button style={btn(false)} title={view === "week" ? "Forward one week" : "Forward"}
                onClick={() => move(1)}>›</button>
        {view === "week" && (
          <button style={btn(false)} title="Forward one month"
                  onClick={() => { const c = new Date(cursor); c.setMonth(c.getMonth() + 1); setCursor(c); }}>»</button>
        )}

        <select value={cursor.getMonth()} style={{ padding: 5 }}
          onChange={(e) => setCursor(new Date(cursor.getFullYear(), +e.target.value, 1))}>
          {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>
        <select value={cursor.getFullYear()} style={{ padding: 5 }}
          onChange={(e) => setCursor(new Date(+e.target.value, cursor.getMonth(), 1))}>
          {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i)
            .map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <span style={{ marginLeft: 8 }}>
          <button style={btn(view === "week")} onClick={() => setView("week")}>Week</button>
          <button style={btn(view === "month")} onClick={() => setView("month")}>Month</button>
          <button style={btn(view === "year")} onClick={() => setView("year")}>Year</button>
        </span>

        <span style={{ marginLeft: 12 }}>
          {["working", "holiday", "absent", "off"].map((k) => (
            <label key={k} style={{ marginRight: 10, cursor: "pointer", fontSize: "0.9em" }}>
              <input type="checkbox" checked={show[k]}
                onChange={() => setShow({ ...show, [k]: !show[k] })} />
              {" "}{k === "off" ? "Not scheduled" : pretty(k)}
            </label>
          ))}
        </span>

        <span style={{ marginLeft: 12, fontSize: "0.9em" }}
              title="A day is shown red when the cover available falls below this.">
          Acceptable cover
          <input type="number" min="0" max="100" value={target}
                 onChange={(e) => setTarget(Math.max(0, Math.min(100, +e.target.value || 0)))}
                 style={{ width: 58, padding: 4, margin: "0 4px" }} />%
          <select value={basis} onChange={(e) => setBasis(e.target.value)}
                  style={{ padding: 4, marginLeft: 4 }}>
            <option value="auto">of hours where known</option>
            <option value="hours">of hours</option>
            <option value="days">of headcount</option>
          </select>
        </span>

        {/* Which roles decide whether a day is short. Everything still shows in
            the grid; this only chooses what the colour is judged on. */}
        {(data?.roles || []).length > 0 && (
          <span style={{ marginLeft: 12, fontSize: "0.85em" }}
                title="Roles that count towards the cover figure. Untick the ones whose absence would not stop the branch running.">
            Judge on:
            {(data.roles).filter((r) => r.name).map((r) => {
              const on = !coverRoles || coverRoles.includes(r.name);
              return (
                <label key={r.id} style={{ marginLeft: 6, cursor: "pointer",
                                           color: roleColour.get(r.id) || "#334155" }}>
                  <input type="checkbox" checked={on}
                    onChange={() => {
                      const all = data.roles.filter((x) => x.name).map((x) => x.name);
                      const cur = coverRoles || all;
                      const next = on ? cur.filter((n) => n !== r.name) : [...cur, r.name];
                      setCoverRoles(next.length === all.length ? null : next);
                    }} />
                  {r.name}
                </label>
              );
            })}
          </span>
        )}

        <span style={{ marginLeft: "auto" }}>
          <button style={{ ...btn(false), background: "#1560ac", color: "#fff" }}
                  onClick={() => setAddOpen("leave")}>+ Holiday</button>
          <button style={{ ...btn(false), background: "#b45309", color: "#fff" }}
                  onClick={() => setAddOpen("absence")}>+ Absence</button>
        </span>
      </div>

      {error && (
        <div style={{ background: "#fff4f4", border: "1px solid #e0b4b4",
                      padding: "8px 10px", marginBottom: 10 }}>{error}</div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          {loading && <div style={{ padding: 12 }}>Loading rota…</div>}

          {!loading && data && view !== "year" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)",
                          gap: 1, background: "#e2e8f0", border: "1px solid #e2e8f0" }}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} style={{ background: "#f8fafc", padding: "6px 8px",
                                      fontWeight: "bold", fontSize: "0.85em" }}>{d}</div>
              ))}
              {gridDays.map((d) => {
                const key = iso(d);
                const inMonth = view === "week" || d.getMonth() === cursor.getMonth();
                const st = dayStats(key);
                const today = key === iso(new Date());
                return (
                  <div key={key} onClick={() => setDayDetail(key)}
                    style={{
                      background: st.hasAnyone ? coverTint(st.coverage)
                                               : (inMonth ? "#fff" : "#fafafa"),
                      minHeight: view === "week" ? 300 : 118,
                      padding: 5, cursor: "pointer",
                      outline: today ? "2px solid #1560ac" : "none",
                      opacity: inMonth ? 1 : 0.45, overflow: "hidden",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "baseline" }}>
                      <span style={{ fontSize: "0.8em", fontWeight: "bold", color: "#475569" }}>
                        {d.getDate()}
                      </span>
                      {st.coverage !== null && (
                        <span style={{ fontSize: "0.72em", fontWeight: "bold",
                                       color: st.coverage < target ? "#b91c1c" : "#15803d" }}
                              title={`Weakest role: ${st.weakest} at ${st.coverage.toFixed(0)}%`}>
                          {st.coverage.toFixed(0)}%
                          {st.coverage < target && st.weakest && (
                            <span style={{ fontWeight: "normal" }}> {st.weakest}</span>
                          )}
                        </span>
                      )}
                    </div>

                    {/* roles, not names: a month cell has no room for a team
                        and the shape of the cover is what matters at a glance */}
                    {st.roles.length === 0 ? (
                      <div style={{ fontSize: "0.7em", color: "#94a3b8", marginTop: 4 }}>
                        nobody scheduled
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse",
                                      fontSize: "0.68em", marginTop: 3 }}>
                        <thead>
                          <tr style={{ color: "#64748b" }}>
                            <th style={{ textAlign: "left", fontWeight: "normal" }}>Role</th>
                            <th title="Scheduled">S</th>
                            <th title="On holiday">H</th>
                            <th title="Absent">A</th>
                            <th title="Actually available">✓</th>
                            <th title="Cover for this role">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {st.roles.map((r) => (
                            <tr key={r.role}>
                              <td style={{ color: roleColour.get(r.roleId) || "#334155",
                                           fontWeight: "bold", whiteSpace: "nowrap",
                                           overflow: "hidden", textOverflow: "ellipsis",
                                           maxWidth: 70 }}>{r.role}</td>
                              <td style={{ textAlign: "center" }}>{r.scheduled}</td>
                              <td style={{ textAlign: "center",
                                           color: r.holiday ? "#1560ac" : "#cbd5e1" }}>{r.holiday}</td>
                              <td style={{ textAlign: "center",
                                           color: r.absent ? "#dc2626" : "#cbd5e1" }}>{r.absent}</td>
                              <td style={{ textAlign: "center", fontWeight: "bold" }}>{r.working}</td>
                              <td style={{ textAlign: "right",
                                           color: r.coverage === null ? "#cbd5e1"
                                                : r.coverage < target ? "#b91c1c" : "#15803d" }}>
                                {r.coverage === null ? "" : `${r.coverage.toFixed(0)}%`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* week view has the room to name people underneath */}
                    {view === "week" && st.hasAnyone && (
                      <div style={{ marginTop: 6, borderTop: "1px solid #e2e8f0", paddingTop: 4 }}>
                        {(byDay[key] || []).filter(visible).map((e, i) => (
                          <div key={i} style={{
                            fontSize: "0.7em", marginBottom: 2, whiteSpace: "nowrap",
                            overflow: "hidden", textOverflow: "ellipsis",
                            opacity: e.source === "assumed" ? 0.65 : 1,
                            fontStyle: e.pending ? "italic" : "normal",
                          }}>
                            <span style={{ color: e.colour }}>●</span>{" "}
                            <span style={{ color: roleColour.get(e.staff.roleId) || "#334155" }}>
                              {displayName(e.staff.name, masked)}
                            </span>
                            {e.state !== "working" && (
                              <span style={{ color: "#64748b" }}> · {e.label}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && data && view === "year" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              {MONTHS.map((m, mi) => {
                const days = [];
                const last = new Date(cursor.getFullYear(), mi + 1, 0).getDate();
                for (let dd = 1; dd <= last; dd++) days.push(new Date(cursor.getFullYear(), mi, dd));
                return (
                  <div key={m} style={{ border: "1px solid #e2e8f0", padding: 6 }}>
                    <div style={{ fontWeight: "bold", fontSize: "0.85em", marginBottom: 4 }}>{m}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                      {days.map((d) => {
                        // same measure as the month view, so a red square here
                        // means exactly what a red cell means there
                        const st = dayStats(iso(d));
                        return (
                          <div key={iso(d)}
                            title={st.coverage === null
                              ? `${iso(d)} — nobody scheduled`
                              : `${iso(d)} — ${st.coverage.toFixed(0)}% cover (${st.basis})`}
                            onClick={() => setDayDetail(iso(d))}
                            style={{
                              height: 15, fontSize: "0.6em", textAlign: "center",
                              cursor: "pointer", borderRadius: 2,
                              background: st.hasAnyone ? coverTint(st.coverage) : "#f1f5f9",
                              color: "#334155",
                            }}>{d.getDate()}</div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* right-hand panel: requests, history and allowance */}
        <div style={{ flex: "0 0 340px", border: "1px solid #ccc", maxHeight: "76vh",
                      overflowY: "auto" }}>
          <div style={{ display: "flex", borderBottom: "1px solid #ddd" }}>
            {[["outstanding", `Outstanding (${pending.length})`],
              ["completed", `Completed (${completed.length})`],
              ["allowance", "Allowance"]].map(([k, label]) => (
              <button key={k} onClick={() => setPanelTab(k)}
                style={{
                  flex: 1, padding: "7px 4px", cursor: "pointer", fontSize: "0.82em",
                  border: 0, borderBottom: panelTab === k ? "3px solid #1560ac" : "3px solid transparent",
                  background: panelTab === k ? "#eff6ff" : "#f6f8fa",
                  color: panelTab === k ? "#1560ac" : "#334155",
                  fontWeight: panelTab === k ? "bold" : "normal",
                }}>{label}</button>
            ))}
          </div>

          {panelTab === "outstanding" && (
            pending.length === 0 ? (
              <div style={{ padding: 12, color: "#666", fontSize: "0.9em" }}>
                Nothing awaiting a decision in this window.
              </div>
            ) : pending.map((l) => {
              const s = staffById.get(l.staffId);
              return (
                <div key={l.id} onClick={() => setReqDetail(l)}
                     style={{ padding: "8px 10px", borderBottom: "1px solid #eee",
                              cursor: "pointer" }}>
                  <div style={{ fontWeight: "bold", fontSize: "0.9em" }}>
                    {displayName(s?.name || "Unknown", masked)}
                    {s?.role && <span style={{ fontWeight: "normal", color: "#64748b" }}> · {s.role}</span>}
                  </div>
                  <div style={{ fontSize: "0.85em", color: "#475569" }}>
                    {l.days.map((d) => isoUTC(d.date)).join(", ")}
                  </div>
                  <div style={{ fontSize: "0.8em", color: "#64748b" }}>
                    {pretty(l.days[0]?.leaveType)}{l.reason ? ` — ${l.reason}` : ""}
                    <span style={{ color: "#1560ac" }}> · click to review</span>
                  </div>
                </div>
              );
            })
          )}

          {panelTab === "completed" && (
            completed.length === 0 ? (
              <div style={{ padding: 12, color: "#666", fontSize: "0.9em" }}>
                No decided requests overlapping this period.
              </div>
            ) : completed.map((l) => {
              const s = staffById.get(l.staffId);
              const ok = l.status === "APPROVED";
              return (
                <div key={l.id} style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
                  <div style={{ fontWeight: "bold", fontSize: "0.9em" }}>
                    {displayName(s?.name || "Unknown", masked)}
                  </div>
                  <div style={{ fontSize: "0.85em", color: "#475569" }}>
                    {l.days.map((d) => isoUTC(d.date)).join(", ")}
                  </div>
                  <span style={{ fontSize: "0.78em", padding: "1px 6px", borderRadius: 3,
                                 background: ok ? "#dcfce7" : "#fee2e2",
                                 color: ok ? "#15803d" : "#b91c1c" }}>
                    {pretty(l.status)}
                  </span>
                </div>
              );
            })
          )}

          {panelTab === "allowance" && (
            !allowance ? (
              <div style={{ padding: 12, color: "#666", fontSize: "0.9em" }}>Loading…</div>
            ) : (
              <>
                <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee",
                              fontSize: "0.8em", color: "#475569" }}>
                  Leave year {allowance.year}. Where no allowance is on file it is
                  taken as {allowance.statutoryWeeks} weeks of the contracted week,
                  the statutory minimum — shown as <em>derived</em>.
                  <div style={{ marginTop: 6 }}>
                    <button onClick={emailAllOutstanding}
                            style={{ background: "#1560ac", color: "#fff", border: 0,
                                     borderRadius: 4, padding: "5px 9px", cursor: "pointer",
                                     fontSize: "0.95em" }}>
                      Email staff outstanding
                    </button>
                  </div>
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82em" }}>
                  <thead>
                    <tr style={{ background: "#fafafa" }}>
                      <th style={{ textAlign: "left", padding: 6, cursor: "pointer" }}
                          onClick={() => setAllowSort({ by: "name",
                            dir: allowSort.by === "name" ? -allowSort.dir : 1 })}>
                        Staff {allowSort.by === "name" ? (allowSort.dir > 0 ? "▲" : "▼") : ""}
                      </th>
                      <th style={{ textAlign: "right", padding: 6 }}>Allow</th>
                      <th style={{ textAlign: "right", padding: 6 }}>Taken</th>
                      <th style={{ textAlign: "right", padding: 6, cursor: "pointer" }}
                          onClick={() => setAllowSort({ by: "remaining",
                            dir: allowSort.by === "remaining" ? -allowSort.dir : 1 })}>
                        Left {allowSort.by === "remaining" ? (allowSort.dir > 0 ? "▲" : "▼") : ""}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowanceSorted.map((r) => (
                      <tr key={r.staffId} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: 6 }}>
                          {displayName(r.name, masked)}
                          {!r.email && (
                            <span style={{ color: "#b45309", fontSize: "0.9em" }} title="No email address on file">
                              {" "}✉
                            </span>
                          )}
                          <div style={{ color: "#94a3b8", fontSize: "0.9em" }}>
                            {r.role}
                            {r.allowanceSource === "derived" && " · derived"}
                            {r.allowanceSource === "unknown" && " · no pattern"}
                          </div>
                        </td>
                        <td style={{ padding: 6, textAlign: "right" }}>
                          {r.allowanceHours == null ? "—" : r.allowanceHours.toFixed(1)}
                        </td>
                        <td style={{ padding: 6, textAlign: "right" }}>
                          {r.takenHours.toFixed(1)}
                          {r.pendingHours > 0 && (
                            <div style={{ color: "#a05a00", fontSize: "0.85em" }}
                                 title="Requested but not yet approved">
                              +{r.pendingHours.toFixed(1)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: 6, textAlign: "right", fontWeight: "bold",
                                     color: r.remainingHours == null ? "#94a3b8"
                                          : r.remainingHours < 0 ? "#b91c1c"
                                          : r.remainingHours === 0 ? "#15803d" : "#0f172a" }}>
                          {r.remainingHours == null ? "—" : r.remainingHours.toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )
          )}
        </div>
      </div>

      {/* day detail — opened by clicking a cell in any view */}
      {dayDetail && (() => {
        const st = dayStats(dayDetail);
        const entries = (byDay[dayDetail] || []);
        const byRole = new Map();
        for (const e of entries) {
          const r = e.staff.role || "No role";
          if (!byRole.has(r)) byRole.set(r, []);
          byRole.get(r).push(e);
        }

        return (
          <div onClick={() => setDayDetail(null)}
               style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 100 }}>
            <div onClick={(ev) => ev.stopPropagation()}
                 style={{ background: "#fff", borderRadius: 6, width: 560,
                          maxHeight: "80vh", overflowY: "auto", padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "flex-start" }}>
                <div>
                  <h3 style={{ margin: 0 }}>
                    {new Date(dayDetail).toLocaleDateString("en-GB",
                      { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </h3>
                  <div style={{ fontSize: "0.85em", color: "#64748b" }}>
                    {displayName(activeBranch?.name, masked)}
                  </div>
                </div>
                <button onClick={() => setDayDetail(null)}
                        style={{ background: "#fff", color: "#0f172a",
                                 border: "1px solid #cbd5e1", borderRadius: 4,
                                 padding: "4px 10px" }}>Close</button>
              </div>

              {/* the cover figure, and what it is measured against */}
              <div style={{ margin: "12px 0", padding: "8px 10px", borderRadius: 4,
                            background: st.hasAnyone ? coverTint(st.coverage) : "#f1f5f9" }}>
                {st.coverage === null ? (
                  <span style={{ color: "#64748b" }}>Nobody is scheduled on this day.</span>
                ) : (
                  <>
                    <strong style={{ fontSize: "1.1em" }}>{st.coverage.toFixed(0)}% cover</strong>
                    <span style={{ color: "#475569" }}>
                      {" "}against a {target}% target
                      {st.coverage < target ? " — short" : " — met"}
                      {st.weakest && `, limited by ${st.weakest}`}
                    </span>
                    <div style={{ fontSize: "0.85em", color: "#475569", marginTop: 2 }}>
                      {st.basis === "hours"
                        ? `${st.availableHours.toFixed(1)}h available of ${st.plannedHours.toFixed(1)}h scheduled`
                        : `${st.working} of ${st.scheduled} scheduled staff available`}
                      {st.holiday > 0 && ` · ${st.holiday} on holiday`}
                      {st.absent > 0 && ` · ${st.absent} absent`}
                    </div>
                  </>
                )}
              </div>

              {[...byRole.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([role, list]) => {
                const rs = st.roles.find((r) => r.role === role);
                return (
                  <div key={role} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: "bold",
                                  color: roleColour.get(list[0].staff.roleId) || "#334155",
                                  borderBottom: "1px solid #e2e8f0", paddingBottom: 3 }}>
                      {role}
                      {rs && (
                        <span style={{ fontWeight: "normal", color: "#64748b",
                                       fontSize: "0.85em" }}>
                          {"  "}{rs.working}/{rs.scheduled} available
                          {rs.plannedHours > 0 &&
                            ` · ${rs.availableHours.toFixed(1)}h of ${rs.plannedHours.toFixed(1)}h`}
                        </span>
                      )}
                    </div>
                    {list.map((e, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between",
                                            padding: "3px 0", fontSize: "0.9em",
                                            opacity: e.state === "off" ? 0.55 : 1 }}>
                        <span>
                          {displayName(e.staff.name, masked)}
                          {e.detail && (
                            <span style={{ color: "#94a3b8", fontSize: "0.85em" }}>
                              {" "}— {e.detail}
                            </span>
                          )}
                        </span>
                        <span style={{ whiteSpace: "nowrap", marginLeft: 10 }}>
                          <span style={{ color: e.colour, fontWeight: "bold" }}>
                            {e.state === "working" ? "Working"
                              : e.state === "holiday" ? "Holiday"
                              : e.state === "absent" ? "Absent" : "Not scheduled"}
                          </span>
                          <span style={{ color: "#64748b" }}>
                            {" "}{e.hours > 0 ? `${e.hours.toFixed(1)}h` : "0h"}
                          </span>
                          <span style={{ color: "#cbd5e1", fontSize: "0.85em" }}>
                            {" "}({e.source})
                          </span>
                          {/* v2: take this one day off the record. Only leave
                              and absence rows have a record to act on —
                              "working" and "not scheduled" are derived, not
                              stored, so there is nothing there to remove. */}
                          {e.recordId && (e.state === "holiday" || e.state === "absent") && (
                            <button
                              onClick={() => setRemoveTarget(e)}
                              title={`Remove this ${e.state === "holiday" ? "holiday" : "absence"} day for ${e.staff.name}`}
                              style={{ marginLeft: 8, background: "#fff",
                                       color: "#b91c1c", border: "1px solid #fca5a5",
                                       borderRadius: 4, padding: "0 6px",
                                       cursor: "pointer", fontSize: "0.95em",
                                       lineHeight: 1.6 }}>✕</button>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}

              {/* What has been changed on this day, and by whom. Shown here
                  rather than behind another click: the question "who took
                  that holiday off?" is asked in exactly this dialogue. */}
              {dayLog && dayLog.length > 0 && (
                <div style={{ marginTop: 14, borderTop: "1px solid #e2e8f0",
                              paddingTop: 10 }}>
                  <div style={{ fontWeight: "bold", fontSize: "0.9em",
                                color: "#334155", marginBottom: 6 }}>
                    Changes to this day
                  </div>
                  {dayLog.map((l, i) => (
                    <div key={i} style={{ fontSize: "0.82em", color: "#475569",
                                          padding: "3px 0",
                                          borderBottom: i < dayLog.length - 1
                                            ? "1px solid #f1f5f9" : "none" }}>
                      <div>{l.detail}</div>
                      {l.reason && (
                        <div style={{ color: "#64748b", fontStyle: "italic" }}>
                          Reason: {l.reason}
                        </div>
                      )}
                      <div style={{ color: "#94a3b8", fontSize: "0.9em" }}>
                        {new Date(l.ts).toLocaleString("en-GB")}
                        {l.by ? ` — ${l.by}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* confirm removing a single leave/absence day */}
      {removeTarget && (
        <RemoveDayDialog entry={removeTarget} masked={masked}
                         branchName={activeBranch?.name}
                         onCancel={() => setRemoveTarget(null)}
                         onConfirm={(reason) => removeDay(removeTarget, reason)} />
      )}

      {/* review a request: what it would do to cover, day by day */}
      {reqDetail && (() => {
        const l = reqDetail;
        const s = staffById.get(l.staffId);
        const role = s?.role || "No role";

        return (
          <div onClick={() => setReqDetail(null)}
               style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 110 }}>
            <div onClick={(ev) => ev.stopPropagation()}
                 style={{ background: "#fff", borderRadius: 6, width: 560,
                          maxHeight: "80vh", overflowY: "auto", padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>
                Holiday request — {displayName(s?.name || "Unknown", masked)}
              </h3>
              <div style={{ fontSize: "0.9em", color: "#475569", marginBottom: 10 }}>
                {role}{l.reason ? ` · ${l.reason}` : ""}
              </div>

              {/* The point of the review: what each day looks like for THIS
                  person's role if the request goes ahead. The figures already
                  include the request, since a pending day is composed into the
                  grid the same way an approved one is. */}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9em" }}>
                <thead>
                  <tr style={{ background: "#fafafa" }}>
                    <th style={{ textAlign: "left", padding: 6 }}>Day</th>
                    <th style={{ textAlign: "left", padding: 6 }}>Type</th>
                    <th style={{ textAlign: "right", padding: 6 }}>{role} cover</th>
                  </tr>
                </thead>
                <tbody>
                  {l.days.map((d, i) => {
                    const key = isoUTC(d.date);
                    const st = dayStats(key);
                    const rs = st.roles.find((r) => r.role === role);
                    const cov = rs?.coverage ?? null;
                    const short = cov !== null && cov < target;
                    return (
                      <tr key={i} style={{ borderTop: "1px solid #eee",
                                           background: cov === null ? "transparent"
                                                     : short ? "#fee2e2" : "#dcfce7" }}>
                        <td style={{ padding: 6 }}>
                          {new Date(key).toLocaleDateString("en-GB",
                            { weekday: "short", day: "numeric", month: "short" })}
                        </td>
                        <td style={{ padding: 6 }}>{pretty(d.leaveType)}</td>
                        <td style={{ padding: 6, textAlign: "right", fontWeight: "bold",
                                     color: cov === null ? "#94a3b8"
                                          : short ? "#b91c1c" : "#15803d" }}>
                          {cov === null ? "—" : `${cov.toFixed(0)}%`}
                          {rs && (
                            <span style={{ fontWeight: "normal", color: "#64748b" }}>
                              {" "}({rs.working}/{rs.scheduled})
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div style={{ fontSize: "0.82em", color: "#64748b", margin: "8px 0 14px" }}>
                Red is below the {target}% target you have set for {role} cover.
                The figures already include this request.
              </div>

              <div style={{ textAlign: "right" }}>
                <button onClick={() => setReqDetail(null)}
                        style={{ background: "#fff", color: "#0f172a",
                                 border: "1px solid #cbd5e1", borderRadius: 4,
                                 padding: "6px 12px", marginRight: 8 }}>Cancel</button>
                <button onClick={() => decideRequest(l, "REJECTED")}
                        style={{ background: "#dc2626", color: "#fff", border: 0,
                                 borderRadius: 4, padding: "6px 12px", marginRight: 8 }}>
                  Reject
                </button>
                <button onClick={() => decideRequest(l, "APPROVED")}
                        style={{ background: "#16a34a", color: "#fff", border: 0,
                                 borderRadius: 4, padding: "6px 12px" }}>
                  Approve
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* what happened, and what was sent */}
      {actionResult && (
        <div onClick={() => setActionResult(null)}
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      zIndex: 120 }}>
          <div onClick={(ev) => ev.stopPropagation()}
               style={{ background: "#fff", borderRadius: 6, width: 440, padding: 18 }}>
            {actionResult.bulk ? (
              <>
                <h3 style={{ marginTop: 0 }}>Allowance reminders sent</h3>
                <p style={{ fontSize: "0.95em" }}>
                  {actionResult.sent} message{actionResult.sent === 1 ? "" : "s"} sent
                  and recorded in each staff member&rsquo;s messages.
                </p>
                {actionResult.note && (
                  <p style={{ color: "#a05a00", fontSize: "0.9em" }}>{actionResult.note}</p>
                )}
                {actionResult.failed?.length > 0 && (
                  <div style={{ color: "#b91c1c", fontSize: "0.85em" }}>
                    <strong>Not sent:</strong>
                    <ul style={{ margin: "4px 0 0 18px" }}>
                      {actionResult.failed.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 style={{ marginTop: 0 }}>
                  Request {actionResult.status === "APPROVED" ? "approved" : "rejected"}
                </h3>
                <p style={{ fontSize: "0.95em" }}>
                  {displayName(actionResult.name, masked)} &mdash; {actionResult.days}
                </p>
                {actionResult.emailed ? (
                  <p style={{ fontSize: "0.92em", color: "#15803d" }}>
                    An email has been sent telling them the outcome
                    {actionResult.remaining != null &&
                      ` and that ${actionResult.remaining.toFixed(1)} hours of allowance remain`}
                    . It is recorded in their messages.
                  </p>
                ) : (
                  <p style={{ fontSize: "0.92em", color: "#b91c1c" }}>
                    The decision is saved, but no email was sent
                    {actionResult.emailError ? ` — ${actionResult.emailError}` : ""}.
                  </p>
                )}
              </>
            )}
            <div style={{ textAlign: "right", marginTop: 12 }}>
              <button onClick={() => setActionResult(null)}
                      style={{ background: "#1560ac", color: "#fff", border: 0,
                               borderRadius: 4, padding: "6px 14px" }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <AddDialog kind={addOpen} branch={activeBranch} staff={data?.staff || []}
                   post={post} masked={masked}
                   onClose={(changed) => {
                     setAddOpen(null);
                     if (changed) setCursor(new Date(cursor));
                   }} />
      )}
    </div>
  );
};

/** Add leave or an absence. Builds the same days[] shape the other app writes. */
/**
 * Confirming the removal of ONE leave or absence day.
 *
 * States plainly what will happen rather than asking "Are you sure?" — the
 * person, the date, and that only this day goes. The reason box is optional
 * but it is the first thing anyone wants three weeks later, so it is offered
 * every time rather than buried.
 */
const RemoveDayDialog = ({ entry, masked, branchName, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const isLeave = entry.state === "holiday";
  const noun = isLeave ? "holiday" : "absence";

  const go = async () => {
    if (busy) return;
    setBusy(true); setErr(null);
    try { await onConfirm(reason.trim()); }
    catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div onClick={() => !busy && onCancel()}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  zIndex: 200 }}>
      <div onClick={(ev) => ev.stopPropagation()}
           style={{ background: "#fff", borderRadius: 6, width: 460,
                    maxWidth: "92vw", padding: 18 }}>
        <h3 style={{ margin: "0 0 10px" }}>Remove this {noun} day?</h3>

        <div style={{ background: "#fef2f2", border: "1px solid #fecaca",
                      borderRadius: 4, padding: "10px 12px", fontSize: "0.9em",
                      color: "#7f1d1d", marginBottom: 12 }}>
          <div>
            <strong>{displayName(entry.staff.name, masked)}</strong>
            {" — "}{entry.label}
          </div>
          <div>
            {new Date(entry.dateKey).toLocaleDateString("en-GB",
              { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            {branchName ? ` · ${displayName(branchName, masked)}` : ""}
          </div>
          <div style={{ marginTop: 6 }}>
            Only this day is removed. Any other days on the same{" "}
            {isLeave ? "request" : "absence"} are left as they are — and if this
            is the only day, the whole {isLeave ? "request" : "record"} is
            withdrawn. There is no undo button: putting it back means booking it
            again.
          </div>
        </div>

        <label style={{ display: "block", fontSize: "0.85em", color: "#475569",
                        marginBottom: 4 }}>
          Reason (optional — recorded in the log)
        </label>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder="e.g. cancelled at the staff member's request"
               style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px",
                        border: "1px solid #cbd5e1", borderRadius: 4,
                        fontSize: "0.9em", marginBottom: 12 }} />

        {err && (
          <div style={{ color: "#b91c1c", fontSize: "0.85em", marginBottom: 10 }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} disabled={busy}
                  style={{ background: "#fff", color: "#0f172a",
                           border: "1px solid #cbd5e1", borderRadius: 4,
                           padding: "6px 12px" }}>Cancel</button>
          <button onClick={go} disabled={busy}
                  style={{ background: "#dc2626", color: "#fff", border: "none",
                           borderRadius: 4, padding: "6px 12px",
                           opacity: busy ? 0.6 : 1 }}>
            {busy ? "Removing…" : `Remove this ${noun} day`}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddDialog = ({ kind, branch, staff, post, masked, onClose }) => {
  const [staffId, setStaffId] = useState("");
  const [type, setType] = useState(kind === "leave" ? "FULL_DAY" : "SICK_LEAVE");
  const [from, setFrom] = useState(iso(new Date()));
  const [to, setTo] = useState(iso(new Date()));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const types = kind === "leave"
    ? ["FULL_DAY", "HALF_DAY_MORNING", "HALF_DAY_AFTERNOON", "CUSTOM_HOURS"]
    : ["SICK_LEAVE", "UNAUTHORISED", "EMERGENCY", "OTHER"];

  const submit = async () => {
    if (!staffId) { setErr("Choose a staff member"); return; }
    if (new Date(to) < new Date(from)) { setErr("End date is before the start date"); return; }

    setBusy(true); setErr(null);
    try {
      // one entry per day in the range, matching the shape already in use
      const days = [];
      const c = new Date(from);
      const end = new Date(to);
      while (c <= end) {
        days.push(kind === "leave"
          ? { date: iso(c), leaveType: type }
          : { date: iso(c), absenceType: type });
        c.setDate(c.getDate() + 1);
      }

      await post(kind === "leave" ? "/api/holidayRota/addLeave" : "/api/holidayRota/addAbsence", {
        dbName: branch.dbName,
        pharmacyId: branch.pharmacyId,
        staffId, days, startDate: from, endDate: to,
        ...(kind === "leave" ? { reason: note || null, status: "APPROVED" } : { notes: note }),
      });
      onClose(true);
    } catch (e) {
      setBusy(false);
      setErr(`Could not save: ${e.message}`);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div style={{ background: "#fff", padding: 18, width: 400, borderRadius: 6 }}>
        <h3 style={{ marginTop: 0 }}>
          {kind === "leave" ? "Add holiday" : "Record absence"}
        </h3>

        <label style={{ fontSize: "0.9em" }}>Staff member</label>
        <select value={staffId} onChange={(e) => setStaffId(e.target.value)}
                style={{ width: "100%", padding: 6, marginBottom: 10 }}>
          <option value="">Select…</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {displayName(s.name, masked)}{s.role ? ` — ${s.role}` : ""}
            </option>
          ))}
        </select>

        <label style={{ fontSize: "0.9em" }}>Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)}
                style={{ width: "100%", padding: 6, marginBottom: 10 }}>
          {types.map((t) => <option key={t} value={t}>{pretty(t)}</option>)}
        </select>

        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.9em" }}>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                   style={{ width: "100%", padding: 6 }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: "0.9em" }}>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                   style={{ width: "100%", padding: 6 }} />
          </div>
        </div>

        <label style={{ fontSize: "0.9em" }}>
          {kind === "leave" ? "Reason (optional)" : "Notes (optional)"}
        </label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  style={{ width: "100%", padding: 6, marginBottom: 10 }} />

        {err && <div style={{ color: "#dc2626", fontSize: "0.9em", marginBottom: 8 }}>{err}</div>}

        <div style={{ textAlign: "right" }}>
          <button onClick={() => onClose(false)} disabled={busy}
                  style={{ padding: "6px 12px", marginRight: 8, background: "#fff",
                           color: "#0f172a", border: "1px solid #cbd5e1",
                           borderRadius: 4 }}>Cancel</button>
          <button onClick={submit} disabled={busy}
                  style={{ padding: "6px 12px", background: "#1560ac", color: "#fff",
                           border: 0, borderRadius: 4 }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HolidayRota;